import type { Theme } from '@mui/material/styles';
import type * as React from 'react';
import type { CSSObject } from 'tss-react';
import { withStyles as tssWithStyles } from 'tss-react/mui';

type ReactComponent<Props> = ((props: Props) => ReturnType<React.FC>) | React.ComponentClass<Props>;

type Style = (theme: Theme, props: any, classes: any) => Record<string, CSSObject>;

// utility function to minimise change when migrating to tss-react
// recommend using tss-react directly for any new uses and preferably makeStyles
export const withStyles =
    (styles: Style) =>
    <C extends ReactComponent<any>, Props extends (C extends ReactComponent<infer P> ? P : never)>(
        component: C,
    ): ReactComponent<Omit<Props, 'classes'> & { classes?: any }> =>
        tssWithStyles(component, styles);

// tss-react does not have a WithStyles type that MUIv4 had so added them
// ideally the withStyles HOC would export the types correctly but functions like compose
// are unable to infer the types correctly or at all
// there are 2 types, for internal and external to the component
// i.e
/*
    function MyComponentBase(props: WithStyles<typeof styles> ) {
        // all style keys will exist on classes prop
        const { classes } = props
        return ( ... )
    }

    export MyComponent: WithStylesOverride<typeof styles> = withStyles(styles)(MyComponentBase)

    function ParentComponent(props) {
        //
        return (
            // can optionally choose to override a Subset of the classes
            // so the keys must still exist in the styles
            <MyComponent classes={{ test: overrideStyle }}>
        )
    }
*/
export interface WithStyles<T extends (...args: any) => any> {
    classes: Record<keyof ReturnType<T>, string>;
}

export interface WithStylesOverride<T extends (...args: any) => any> {
    classes?: Partial<Record<keyof ReturnType<T>, string>>;
}
